"use client";

import { useEffect, useRef } from "react";

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement, forceReload?: boolean) => void;
    };
  }
}

const BUSINESS_UNIT_ID = "65c1229b4fb68272602cbbd8";
const REVIEW_URL = "https://www.trustpilot.com/review/pandahub.com";

const VARIANTS = {
  /** Micro Star strip, fixed 420px — pair with md:block hidden */
  microDesktop: {
    "data-template-id": "5419b6ffb0d04a076446a9af",
    "data-style-height": "20px",
    "data-style-width": "420px",
    "data-token": "c2782902-3408-46c2-8495-c6c2310114e8",
    fallbackText: "Rated 4.8/5 on Trustpilot",
  },
  /** Micro Star strip, full width — pair with md:hidden block */
  microMobile: {
    "data-template-id": "5419b637fa0340045cd0c936",
    "data-style-height": "20px",
    "data-style-width": "100%",
    "data-token": "85b394ee-0286-4792-b82f-af38162a5274",
    fallbackText: "Rated 4.8/5 on Trustpilot",
  },
  /** Mini centered box (reviews sections) */
  mini: {
    "data-template-id": "5419b6a8b0d04a076446a9ad",
    "data-style-height": "27px",
    "data-style-width": "270px",
    "data-token": "173782d8-2f6e-402a-9b88-afd0c8e36a5a",
    "data-min-review-count": "0",
    "data-style-alignment": "center",
    fallbackText: "Trustpilot",
  },
} as const;

interface TrustpilotWidgetProps {
  variant: keyof typeof VARIANTS;
  className?: string;
}

/**
 * Trustpilot TrustBox marker div. The bootstrap script in RootShell converts
 * these to iframes on first full page load, but never re-scans after App
 * Router soft navigations — so this component re-initializes its own element
 * on mount via Trustpilot.loadFromElement.
 *
 * The fallback anchor is rendered with dangerouslySetInnerHTML and
 * suppressHydrationWarning so React adopts the DOM as-is even if the script
 * converts the SSR markup before hydration reaches it (avoids React #418).
 */
export function TrustpilotWidget({ variant, className }: TrustpilotWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { fallbackText, ...dataAttrs } = VARIANTS[variant];

  useEffect(() => {
    const el = ref.current;
    // Skip when the script hasn't loaded yet (its own scan will pick the div
    // up) or when this element already holds a live widget iframe.
    if (el && window.Trustpilot && !el.querySelector("iframe")) {
      window.Trustpilot.loadFromElement(el, true);
    }
  }, []);

  return (
    <div
      ref={ref}
      className={`trustpilot-widget ${className ?? ""}`}
      data-locale="en-US"
      data-businessunit-id={BUSINESS_UNIT_ID}
      {...dataAttrs}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: `<a href="${REVIEW_URL}" target="_blank" rel="noopener">${fallbackText}</a>`,
      }}
    />
  );
}
